/**
 * Display helpers shared by the case screens — AdminCases, CaseDetail and
 * AdminPanel.
 *
 * They lived in AdminCases.tsx, which made that file export a page component and
 * three plain functions at once. React-refresh cannot preserve state across an
 * edit to a module that mixes the two, so every keystroke in the ops queue
 * remounted it. Nothing here renders, so nothing here belongs in a .tsx.
 */
import type { CaseStatus } from "@titleverify/shared";

/**
 * Dates only, never times, and in the reader's own locale. An ops queue is read
 * for "how long has this sat", which a date answers; a timestamp to the second
 * implies a precision about workflow events that nobody here needs.
 */
export function formatDate(iso: string): string {
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? iso : date.toLocaleDateString();
}

/**
 * The states an operator can act on from the detail screen. Shared with CaseDetail.
 *
 * Named `...CaseState` rather than the obvious `isRoutable` / `isAssignable`
 * because `isAssignable` ALREADY EXISTS in @titleverify/shared and means
 * something completely different — whether a PERSON's verification standing lets
 * them take work. Two functions with one name, one about a case and one about an
 * attorney, both imported into the same file, is a mix-up that type-checks.
 *
 * NEITHER OF THESE ENFORCES ANYTHING. They decide whether a control is worth
 * drawing; the server refuses on its own terms and returns a message saying why,
 * which the detail screen renders verbatim. Hiding a control is a courtesy.
 */
export function isRoutableCaseState(status: CaseStatus): boolean {
  return status === "SUBMITTED" || status === "VALIDATING";
}

export function isAssignableCaseState(status: CaseStatus): boolean {
  return status === "ROUTED" || status === "ASSIGNED";
}
